import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import Icon from '../components/AppIcon';

import DashboardSidebar from '../components/navigation/DashboardSidebar';
import DashboardHeader from '../components/navigation/DashboardHeader';
import DashboardFooter from '../components/navigation/DashboardFooter';
import MobileMenuButton from '../components/navigation/MobileMenuButton';

const MyDocuments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnno, setFilterAnno] = useState('Tutti');
  const [filterTipo, setFilterTipo] = useState('Tutti');

  useEffect(() => {
    fetchMieiDocumenti();
  }, []);

  const fetchMieiDocumenti = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // MODIFIKOVANO: Dodat filter .eq('is_deleted', false)
        const { data, error } = await supabase
          .from('documenti')
          .select('*')
          .eq('id_utente', user.id)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        setDocumenti(data || []);
      }
    } catch (err) {
      console.error("Errore fetch documenti:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- LOG AKTIVNOSTI ---
  const logActivity = async (doc, actionType) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profil } = await supabase
        .from('profili')
        .select('nome, cognome, ruolo, id_azienda, email')
        .eq('id', user.id)
        .single();

      const tzOffset = (new Date()).getTimezoneOffset() * 60000; 
      const localISOTime = (new Date(Date.now() - tzOffset)).toISOString().slice(0, -1);

      await supabase.from('audit_logs').insert([{
        id_azienda: profil?.id_azienda,
        executor_id: user.id,
        executor_uuid_text: user.id,
        executor_name: `${profil?.nome || ''} ${profil?.cognome || ''}`.trim(),
        executor_email: profil?.email || user.email,
        executor_role: profil?.ruolo || 'user',
        target_user_id: doc.id_utente,
        target_uuid_text: doc.id_utente,
        action: actionType,
        file_name: doc.nome_file,
        file_id: doc.id,
        user_agent: navigator.userAgent,
        ip_address: 'Client-side Request',
        created_at: localISOTime 
      }]);
    } catch (err) {
      console.error("Audit Log Error:", err);
    }
  };

  const formatPeriod = (doc) => {
    if (!doc.mese) return doc.anno;
    const translatedMonth = t(`months.${doc.mese}`, { defaultValue: doc.mese });
    return `${translatedMonth.toUpperCase()} ${doc.anno}`;
  };

  // --- DINAMIČKO RENDERIRANJE TIPA DOKUMENTA ---
  const renderTipologia = (tipo) => {
    const standardKeys = {
      'Busta Paga': 'myDocuments.types.bustaPaga',
      'CU': 'myDocuments.types.cu',
      'Contratto': 'myDocuments.types.contratto',
      'Visita Medica': 'myDocuments.types.visitaMedica',
      'Certificato Corso': 'myDocuments.types.certificatoCorso',
      'Altro': 'myDocuments.types.altro'
    };

    const translationKey = standardKeys[tipo];
    
    if (translationKey) {
      return t(translationKey, { defaultValue: tipo });
    }
    
    return tipo; 
  };

  const handleDownload = async (doc) => {
    try {
      setDownloadingId(doc.id);
      const { data, error } = await supabase.storage.from('aziende').download(doc.url_path);
      if (error) throw error;

      if (doc.procitano === false) {
        const { error: updateError } = await supabase
          .from('documenti')
          .update({ procitano: true })
          .eq('id', doc.id);
        
        if (!updateError) {
          setDocumenti(prev => prev.map(d => d.id === doc.id ? { ...d, procitano: true } : d));
        }
      }

      await logActivity(doc, 'DOWNLOAD');

      const blobUrl = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', doc.nome_file);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

    } catch (err) {
      alert(t('myDocuments.errorDownload'));
    } finally {
      setDownloadingId(null);
    }
  };

  const isFiltering = searchTerm !== '' || filterAnno !== 'Tutti' || filterTipo !== 'Tutti';

  const filteredDocumenti = documenti.filter(doc => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = 
      doc.nome_file.toLowerCase().includes(s) || 
      doc.tipologia.toLowerCase().includes(s) ||
      (doc.nome_corso && doc.nome_corso.toLowerCase().includes(s)) ||
      (doc.mese && doc.mese.toLowerCase().includes(s)) ||
      doc.anno.toString().includes(s);
    const matchesAnno = filterAnno === 'Tutti' || doc.anno.toString() === filterAnno;
    const matchesTipo = filterTipo === 'Tutti' || doc.tipologia === filterTipo;
    return matchesSearch && matchesAnno && matchesTipo;
  });

  const docsToDisplay = isFiltering ? filteredDocumenti : documenti.slice(0, 5);
  const uniqueYears = [...new Set(documenti.map(d => d.anno))].sort((a, b) => b - a);
  const uniqueTypes = [...new Set(documenti.map(d => d.tipologia))].sort();

  // Ovdje se nastavlja tvoj return ...
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <MobileMenuButton isOpen={isSidebarOpen} onClick={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardSidebar isCollapsed={!isSidebarOpen} onToggle={() => setIsSidebarOpen(!isSidebarOpen)} />
      <DashboardHeader userName={t('myDocuments.title')} />

      <main className="main-content with-sidebar py-10 px-4 mt-16">
        <div className="max-w-6xl mx-auto space-y-8">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <button 
                onClick={() => navigate(-1)} 
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-bold uppercase text-[10px] tracking-widest mb-4"
              >
                <Icon name="ArrowLeft" size={14} /> {t('myDocuments.backHome')}
              </button>
              <h2 className="text-4xl font-black uppercase tracking-tighter">{t('myDocuments.title')}</h2>
              <p className="text-muted-foreground text-sm font-medium mt-1">{t('myDocuments.subtitle')}</p>
            </div>

            <button 
              onClick={() => navigate('/log-audit-user')}
              className="flex items-center gap-2 px-5 py-3 bg-primary/10 text-primary border border-primary/20 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-primary hover:text-white transition-all duration-300 shadow-sm group"
            >
              <Icon name="Activity" size={16} className="group-hover:animate-pulse" />
              {t('myDocuments.auditLogButton', { defaultValue: 'Audit Monitoring' })}
            </button>
          </div>

          {/* FILTERI */}
          <div className="bg-card p-4 rounded-3xl border border-border shadow-sm flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-[2] w-full">
              <label className="text-[10px] font-black uppercase text-muted-foreground mb-2 block ml-1 tracking-widest">{t('myDocuments.searchLabel')}</label>
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
                <input 
                  type="text" 
                  placeholder={t('myDocuments.searchPlaceholder')}
                  className="w-full pl-12 pr-4 py-3 bg-muted/30 border border-border rounded-2xl text-sm outline-none focus:bg-background transition-all font-bold"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex-1 w-full grid grid-cols-2 gap-3">
               <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground mb-2 block ml-1 tracking-widest">{t('myDocuments.filterAnno')}</label>
                  <select className="w-full p-3 bg-muted/30 border border-border rounded-2xl text-sm outline-none font-bold" value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)}>
                    <option value="Tutti">{t('myDocuments.filterAll')}</option>
                    {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
               </div>
               <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground mb-2 block ml-1 tracking-widest">{t('myDocuments.filterTipo')}</label>
                  <select className="w-full p-3 bg-muted/30 border border-border rounded-2xl text-sm outline-none font-bold" value={filterTipo} onChange={(e) => setFilterTipo(e.target.value)}>
                    <option value="Tutti">{t('myDocuments.filterAll')}</option>
                    {uniqueTypes.map(tipo => (
                      <option key={tipo} value={tipo}>
                        {renderTipologia(tipo)}
                      </option>
                    ))}
                  </select>
               </div>
            </div>
          </div>

          <div className="bg-card rounded-3xl border border-border shadow-xl overflow-hidden">
            {loading ? (
              <div className="p-20 text-center"><Icon name="Loader2" className="animate-spin mx-auto text-primary" size={32} /></div>
            ) : docsToDisplay.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead className="bg-muted/50 text-muted-foreground border-b border-border">
                    <tr>
                      <th className="p-5 font-bold uppercase text-[10px] tracking-widest">{t('myDocuments.table.document')}</th>
                      <th className="p-5 font-bold uppercase text-[10px] tracking-widest">{t('myDocuments.table.type')}</th>
                      <th className="p-5 font-bold uppercase text-[10px] tracking-widest">{t('myDocuments.table.details')}</th>
                      <th className="p-5 text-right font-bold uppercase text-[10px] tracking-widest">{t('myDocuments.table.download')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {docsToDisplay.map(doc => (
                      <tr 
                        key={doc.id} 
                        className={`transition-colors group ${!doc.procitano ? 'bg-primary/5 font-black' : 'hover:bg-muted/20'}`}
                      >
                        <td className="p-5">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg border ${!doc.procitano ? 'bg-primary/20 border-primary/30' : 'bg-background border-border'}`}>
                              <Icon name="FileText" size={18} className={!doc.procitano ? 'text-primary' : 'text-muted-foreground'} />
                            </div>
                            <div className="flex flex-col">
                              <span className={!doc.procitano ? 'text-primary' : 'text-foreground'}>{doc.nome_file}</span>
                              <span className="text-[10px] text-muted-foreground uppercase tracking-tighter">
                                {t('myDocuments.table.uploadedOn')} {new Date(doc.created_at).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-5">
                          <span className={`px-2.5 py-1 rounded-md text-[9px] font-black border uppercase tracking-widest ${
                            doc.tipologia === 'Busta Paga' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' : 
                            doc.tipologia === 'Visita Medica' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                            doc.tipologia === 'Certificato Corso' ? 'bg-purple-500/10 text-purple-500 border-purple-500/20' :
                            'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                          }`}>
                            {renderTipologia(doc.tipologia)}
                          </span>
                        </td>
                        <td className="p-5">
                          <div className="flex flex-col">
                            {doc.nome_corso && <span className="text-xs font-bold text-foreground mb-1">{doc.nome_corso}</span>}
                            {doc.valido_al ? (
                              <div className="flex items-center gap-1.5">
                                <Icon name="Calendar" size={12} className="text-orange-500" />
                                <span className="text-[11px] font-black text-orange-600 uppercase">
                                  {t('myDocuments.table.expires')}: {new Date(doc.valido_al).toLocaleDateString('it-IT')}
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm font-bold text-muted-foreground">
                                {formatPeriod(doc)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-5 text-right">
                          <div className="relative inline-block">
                            <button 
                              onClick={() => handleDownload(doc)}
                              disabled={downloadingId === doc.id}
                              className={`p-3 rounded-xl transition-all shadow-md disabled:opacity-50 hover:scale-110 active:scale-95 ${
                                  !doc.procitano 
                                  ? 'bg-primary text-white shadow-primary/40' 
                                  : 'bg-muted text-muted-foreground hover:bg-primary hover:text-white'
                              }`}
                            >
                              <Icon 
                                  name={downloadingId === doc.id ? "Loader2" : "Download"} 
                                  size={18} 
                                  className={downloadingId === doc.id ? "animate-spin" : ""} 
                              />
                            </button>
                            
                            {!doc.procitano && (
                              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white"></span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-20 text-center">
                <Icon name="FileX" size={48} className="mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground font-medium">{t('myDocuments.table.noDocs')}</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};

export default MyDocuments;